import { createFileRoute } from '@tanstack/react-router';
import { FiCalendar, FiClock, FiRepeat, FiSun } from 'react-icons/fi';
import { SeoLandingPage } from '@/components/marketing';
import harborSsCalendarPng from '@/assets/harbor_ss_calendar.png';

function CustodyScheduleAppPage() {
  return (
    <SeoLandingPage
      path='/custody-schedule-app'
      title='Custody Schedule App for Co-Parents | Harbor'
      description='Share a custody and visitation schedule both parents can see. Set weekly rotations, holidays, and exceptions once, then stop arguing about whose weekend it is.'
      breadcrumbName='Custody schedule app'
      eyebrow='Custody calendar'
      h1='A custody schedule app both parents can share'
      hero='Set up your parenting-time rotation once — week-on/week-off, 2-2-3, or a custom mix — and both parents always know whose day it is. Harbor keeps holidays and one-off swaps next to the regular calendar.'
      screenEvent='Custody Schedule App Screen'
      ctaLocation='Custody Schedule App'
      storeCampaign='custody-schedule-app'
      featuresHeading='Build a schedule you can actually follow'
      features={[
        {
          icon: FiCalendar,
          title: 'One shared custody calendar',
          description:
            'Harbor is a custody schedule app for separated parents who need the same source of truth. The calendar shows parenting time, not a generic family planner stuffed with chores.',
          bullets: [
            'Weekly rotation, 2-2-3, 3-4-4-3, or a custom pattern',
            'Both parents see the same days without texting screenshots',
            'Works if only one parent joins on day one',
          ],
          image: harborSsCalendarPng,
          imageAlt: 'Harbor custody calendar showing a shared parenting-time schedule',
        },
        {
          icon: FiRepeat,
          title: 'Visitation schedule without a second app',
          description:
            'Visitation days, weekends, and mid-week overnights live on the same calendar as 50/50 rotations. You do not need a separate visitation-schedule product.',
          bullets: [
            'Mark school-night vs weekend time clearly',
            'Keep long-distance or every-other-weekend patterns readable',
            'See the next handoff before it becomes an argument',
          ],
        },
        {
          icon: FiSun,
          title: 'Holidays and exceptions stay next to the rotation',
          description:
            'Christmas, spring break, and a birthday swap should not wipe the repeating schedule. Add exceptions without rebuilding the whole calendar.',
          bullets: [
            'Holiday overlays on top of the regular rotation',
            'One-off swaps that do not break next month',
            'A visual week view the whole family can read',
          ],
        },
        {
          icon: FiClock,
          title: 'Parenting time you can export later',
          description:
            'The schedule is useful today and still useful if you need a record. Pair it with Harbor’s court-ready exports when a lawyer or mediator asks what the plan actually was.',
          bullets: [
            'Keep the live calendar in Harbor, not in a lost spreadsheet',
            'Export calendar records with messages and expenses when needed',
            'Draft a written plan in the parenting-plan builder, then run it here',
          ],
        },
      ]}
      faqs={[
        {
          question: 'Is Harbor a custody schedule app or just a calendar?',
          answer:
            'Harbor is built for parenting-time rotations: repeating custody patterns, visitation weekends, holidays, and exceptions. It is not a generic shared calendar for groceries and soccer practice.',
        },
        {
          question: 'Can I use Harbor if the other parent will not download it?',
          answer:
            'Yes. Start solo, keep the schedule, and invite later. You do not need both parents onboarded on day one.',
        },
        {
          question: 'Does Harbor replace a written parenting plan?',
          answer:
            'No. Use the free parenting-plan builder to draft language, then keep the live rotation in this custody schedule app. Harbor is not legal advice.',
        },
        {
          question: 'What custody patterns can I set up?',
          answer:
            'Week-on/week-off, 2-2-3, 3-4-4-3, 50/50, 60/40, every extended weekend, and custom rotations. Holidays and one-off swaps sit on top of the repeating pattern.',
        },
      ]}
      crossLinks={[
        {
          href: '/parenting-plan/builder',
          label: 'Free parenting plan builder',
          description:
            'Turn the rotation into written custody, holiday, and communication language you can download as a PDF.',
        },
        {
          href: '/parenting-plan/week-on-week-off',
          label: 'Week-on / week-off guide',
          description:
            'See how a 7/7 custody schedule looks before you put it on the calendar.',
        },
      ]}
    />
  );
}

export const Route = createFileRoute('/custody-schedule-app')({
  component: CustodyScheduleAppPage,
});
